import "./InstrumentViewer.css";
import React, {ReactElement, useEffect, useState} from "react";
import {InstrumentTableRow} from "../../../../data/services/instrument-table-service";
import {StatusPlotHeader, StatusPlotRow} from "../statusplot/StatusPlot";
import RevioImage from "../../assets/REVIO.png";
import VegaImage from "../../assets/VEGA.svg";
import {ZmwsPlot} from "../../../instruments/instruments-home/ZmwsPlot";
import {getRunPreviewData, RunPreviewData} from "../../../../data/services/run-preview-service";
import {ReusableCell} from "../../../../data/model/instrument-model";

const txt_runname = "Run Name";
const txt_cellstatus = "SMRT Cells";
const txt_runcompletion = "Completion";
const txt_timepreload = "Time until preload :";
const txt_reusablecellstable = "Multi-use SMRTCell status";
const txt_smrtcell = "SMRT Cell";
const txt_startwithin = "Start within";
const txt_remaining = "Remaining uses";
const txt_information = "Information";
const txt_activeruns = "Active Runs";

export interface InstrumentViewerProps {
    instrument: InstrumentTableRow;
}

const InstrumentViewer = (props: InstrumentViewerProps): ReactElement => {
    const SCALE = 0.5;

    let ImageSrc: string | null;
    let imageStyle: React.CSSProperties = {
        margin: "10px",
    };
    switch (props.instrument.instrumentType) {
        case "Revio":
            ImageSrc = RevioImage;
            imageStyle = {
                ...imageStyle,
                filter: "brightness(3.5)",
                width: 101 * SCALE,
                height: 189 * SCALE,
            };
            break;
        case "Vega":
            ImageSrc = VegaImage;
            imageStyle = {
                ...imageStyle,
                filter: "brightness(0.9)",
                width: 142 * SCALE,
                height: 152 * SCALE,
            };
            break;
        default:
            ImageSrc = null;
            imageStyle = null;
    }

    const timePreload = props.instrument.timeUntilPreload
        ? `${txt_timepreload} ${props.instrument.timeUntilPreload}`
        : "";

    const instrumentState = `${props.instrument.state}`;
    let statecolor: string;
    switch (instrumentState) {
        case "Ready":
            statecolor = "var(--green-bright-color)"
            break;
        case "Running":
            statecolor = "var(--blue-medium-color)"
            break;
        case "Problem":
            statecolor = "var(--gray-light-color)"
            break;
    }

    const RunRow: React.FC<{ run: typeof props.instrument.runInfos[number] }> = ({run}) => {
        const [previewData, setPreviewData] = useState<RunPreviewData>(null)

        useEffect(() => {
            const collectionIds = run.collections.map(collection => collection.uniqueId)
            getRunPreviewData(run.runId, collectionIds).then(runPreviewData => {
                setPreviewData(runPreviewData)
            })
        }, [props.instrument]);

        const plotStyle: React.CSSProperties = {
            marginRight: "20px",
            marginBottom: "20px",
            backgroundColor: "var(--palest-gray)"
        };

        return (
            <div key={run.runId} className="instrumentviewer-run">
                <div className="instrumentviewer-run-info">
                    <div className="instrumentviewer-run-infolabel">{txt_runname}</div>
                    <div className="instrumentviewer-run-name">{run.runData.name}</div>
                </div>

                <div className="instrumentviewer-run-info">
                    <div className="instrumentviewer-run-infolabel">{txt_runcompletion}</div>
                    <div className="instrumentviewer-run-completion">{run.runCompletion}</div>
                </div>

                <div className="instrumentviewer-run-info">
                    <div className="instrumentviewer-run-infolabel">{txt_cellstatus}</div>
                    <div className="instrumentviewer-run-statusplot">
                        <StatusPlotHeader/>
                        <StatusPlotRow runInfo={run}/>
                    </div>
                </div>

                <div className="instrumentviewer-run-plots-panel">
                    <ZmwsPlot
                        instrument={props.instrument}
                        runId={run.runId}
                        previewData={previewData}
                        plotStyle={plotStyle}
                    />
                </div>

            </div>
        );
    };

    const formatStartWithin = (cell: ReusableCell): string => {
        if (cell.usesExpirationAlertType === "Expired") {
            return "Expired";
        }

        const now = new Date();
        const expiration = new Date(cell.usesExpirationDate);
        const diffMs = expiration.getTime() - now.getTime();

        if (diffMs <= 0) {
            return "Expired";
        }

        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days} day${days === 1 ? "" : "s"}`;
        }

        return `${hours} hour${hours === 1 ? "" : "s"}`;
    };

    type MultiUseCellsTableProps = {
        reusableCells?: ReusableCell[];
    };

    const ReusableCellsTable = ({reusableCells}: MultiUseCellsTableProps): JSX.Element | null => {
        if (!reusableCells || reusableCells.length === 0) {
            return null;
        }

        return (
            <table className="reusable-cells-table">
                <thead>
                <tr>
                    <th colSpan={4} className="reusable-cells-title">
                        {txt_reusablecellstable}
                    </th>
                </tr>
                <tr>
                    <th>{txt_smrtcell}</th>
                    <th>{txt_startwithin}</th>
                    <th>{txt_remaining}</th>
                    <th>{txt_information}</th>
                </tr>
                </thead>
                <tbody>
                {reusableCells.map((cell) => (
                    <tr key={cell.cellId}>
                        <td>{cell.cellId}</td>
                        <td>{formatStartWithin(cell)}</td>
                        <td>
                            {cell.remainingUses} / {cell.maxUses}
                        </td>
                        <td>{cell.statusMessage}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        );
    };

    interface AlarmProps {
        message: string;
    }

    const AlarmSection = ({message}: AlarmProps): JSX.Element | null => {
        if (!message || message.length === 0)
            return null;

        return (
            <div className="instrumentviewer-header-alarm">
                <div className="instrumentviewer-header-alarm-icon">!</div>
                <div className="instrumentviewer-header-alarm-text">{message}</div>
            </div>
        );
    }

    return (
        <div className="instrumentviewer">
            <div className="instrumentviewer-header">
                {ImageSrc &&
                    <div className="instrumentviewer-header-image">
                        <img src={ImageSrc} alt="" style={imageStyle}/>
                    </div>
                }
                <div className="instrumentviewer-header-info">
                    <div className="instrumentviewer-header-title-text">{props.instrument.name}</div>
                    <div className="instrumentviewer-header-typepreload">
                        <div className="instrumentviewer-header-type-text">{props.instrument.instrumentType}</div>
                        <div className="instrumentviewer-header-preload-text">{timePreload}</div>
                    </div>
                </div>
                <div className="instrumentviewer-header-status-container">
                    <div className="instrumentviewer-header-status">
                        <div className="instrumentviewer-header-status-dot" style={{backgroundColor: statecolor}}/>
                        <div className="instrumentviewer-header-status-text"
                             style={{color: statecolor}}>{instrumentState}</div>
                    </div>
                </div>
            </div>

            {props.instrument.alarmMessage &&
                <AlarmSection message={props.instrument.alarmMessage}/>
            }

            <div className="instrumentviewer-header-multiuse">
                <ReusableCellsTable reusableCells={props.instrument.reusableCells}/>
            </div>

            <div className="instrumentviewer-runs-title">{txt_activeruns}</div>
            <div className="instrumentviewer-runs">
                {props.instrument.runInfos.map((run) => (
                    <RunRow key={run.runId} run={run}/>
                ))}
            </div>
        </div>
    );
};

export default InstrumentViewer;
